import { Application, Container } from 'pixi.js';
import type { FederatedPointerEvent } from 'pixi.js';
import { makeBoard } from './crossword.js';
import type { BoardMode, CrosswordData } from './crossword.js';
import { createGame } from './game.js';
import type { GameState } from './game.js';
import { Tile, GAP } from './tile.js';
import { initLevelSelect } from './levelSelect.js';
import { loadProgress, recordSolve } from './levels.js';
import type { Level } from './levels.js';
import { withSeed } from './rng.js';

// Every mode's size table lives in crossword.ts; all of them are indexed by
// the same S/M/L/XL step.
let sizeIndex = 2; // default L

let gameMode: BoardMode = 'cross';
let diffIndex          = 1;    // 0=Easy, 1=Normal, 2=Hard
const vivid             = true; // OKLCH blending — always on

// Free play rolls a fresh board on demand; progression plays a fixed level off
// the ladder. `level` is what's being played, and null in free play.
type AppMode = 'free' | 'prog';
type Screen  = 'menu' | 'levels' | 'game';
let appMode: AppMode   = 'free';
let level: Level | null = null;

const PAD_X = 20;
// Wider viewports get more breathing room above/below the board — on a
// phone every pixel of vertical space matters, so it stays tight there.
function padTop(): number { return window.innerWidth >= 700 ? 160 : 120; }
// Leaves room for the "Show" peek button and the footer credit line,
// which both sit fixed near the bottom of the viewport.
function padBot(): number { return window.innerWidth >= 700 ? 130 : 100; }

(async () => {

// --- Pixi init ---
const app = new Application();
await app.init({
  preference: 'webgl',
  background: '#0a0a0a',
  backgroundAlpha: 0, // transparent, so the CSS rainbow gradient behind it shows
  resizeTo: window,
  antialias: true,
  // Phones commonly report a devicePixelRatio of 3+; rendering every pixel
  // at that density (on top of everything else) is a lot of fill-rate for
  // very little visible sharpness gain over a 2x cap.
  resolution: Math.min(window.devicePixelRatio || 1, 2),
  autoDensity: true
});
const canvas = app.canvas;
canvas.style.display  = 'block';
canvas.style.position = 'absolute';
canvas.style.top      = '0';
canvas.style.left     = '0';
canvas.style.width    = '100%';
canvas.style.height   = '100%';
canvas.style.touchAction = 'none'; // let a drag be a drag, not a page scroll
document.getElementById('app')!.appendChild(canvas);

// --- Idle framerate throttling ---
// The board is static almost all the time between moves, but every tile
// carries several blurred glow layers, which is not cheap to keep
// re-rendering at 60fps on a phone. Idle sits well below that; anything
// in motion (a swap, a drag, the win pulse) bumps back up to uncapped.
const IDLE_FPS = 20;
let activeAnimations = 0;
app.ticker.maxFPS = IDLE_FPS;
function beginAnim(): void {
  activeAnimations++;
  app.ticker.maxFPS = 0;
}
function endAnim(): void {
  activeAnimations = Math.max(0, activeAnimations - 1);
  if (activeAnimations === 0) app.ticker.maxFPS = IDLE_FPS;
}

// --- State ---
let game: GameState | null = null;
let board: CrosswordData | null = null;
let tiles: Tile[]  = [];
let swapInProgress = false;
let moveCount      = 0;
let gameGen        = 0;
let tileSize       = 0;
let cellIndex      = new Map<number, number>(); // flat grid position → grid index

const moveCountEl   = document.getElementById('move-count')!;
const winOverlay    = document.getElementById('win-overlay')!;
const winHideBtn    = document.getElementById('win-hide')!;
const gridContainer = new Container();
app.stage.addChild(gridContainer);

// --- Layout ---
// Small enough to fit anything, big enough that the board can still be seen
// and touched if the numbers ever come out strange.
const MIN_TILE = 16;
// Below this the viewport isn't a real one — see relayout().
const MIN_VIEWPORT = 200;

function computeTileSize(gridW: number, gridH: number): number {
  const availW = app.screen.width  - PAD_X  * 2;
  const availH = app.screen.height - padTop() - padBot();
  const byW    = (availW - GAP * (gridW - 1)) / gridW;
  const byH    = (availH - GAP * (gridH - 1)) / gridH;
  return Math.max(MIN_TILE, Math.floor(Math.min(byW, byH)));
}

function layoutAll(): void {
  if (!game) return;
  if (app.screen.width < MIN_VIEWPORT || app.screen.height < MIN_VIEWPORT) return;
  const { gridW, gridH } = game;
  tileSize = computeTileSize(gridW, gridH);
  const gridPxW  = gridW * tileSize + (gridW - 1) * GAP;
  const gridPxH  = gridH * tileSize + (gridH - 1) * GAP;
  const top      = padTop();
  const remainH  = app.screen.height - top - padBot();
  gridContainer.x = Math.floor((app.screen.width - gridPxW)  / 2);
  gridContainer.y = Math.floor(top + (remainH - gridPxH) / 2);
  for (const tile of tiles) tile.layout(tileSize);
}

// --- Tile setup ---
function initTiles(): void {
  for (const tile of tiles) {
    tile.container.eventMode = 'none';
    gridContainer.removeChild(tile.container);
  }
  tiles = [];
  drag  = null;

  const { activeCells, gridW, total } = game!;
  cellIndex = new Map(activeCells.map((flatPos, i) => [flatPos, i]));
  for (let i = 0; i < total; i++) {
    const tile = new Tile(i, activeCells, gridW);
    tiles.push(tile);
    gridContainer.addChild(tile.container);
    tile.container.on('pointerdown', e => onTilePointerDown(tile, e));
  }
}

function syncTileColors(): void {
  const g = game!;
  for (let i = 0; i < g.total; i++) {
    tiles[i].setColor(g.colors[g.tileAt[i]], g.isAnchor(i));
    tiles[i].setPreset(g.isPreset(i));
    tiles[i].setSelected(g.selectedIndex === i);
    tiles[i].setLocked(g.isLocked(i));
  }
}

// --- New game ---
function buildBoard(): { board: CrosswordData; game: GameState } {
  const b = makeBoard(gameMode, sizeIndex, diffIndex, vivid);
  // The shuffle is part of the board as far as the player is concerned, so it
  // has to come out of the same seeded stream as the layout and the colours.
  return { board: b, game: createGame(b) };
}

function newGame(): void {
  gameGen++;
  // Nothing from the last board is still in flight once it's been thrown away.
  activeAnimations = 0;
  app.ticker.maxFPS = IDLE_FPS;

  const made = appMode === 'prog' && level
    ? withSeed(level.seed, buildBoard)
    : buildBoard();
  board     = made.board;
  game      = made.game;
  moveCount = 0;
  moveCountEl.textContent = '00';
  swapInProgress = false;

  winOverlay.classList.remove('visible', 'peeking');
  winHideBtn.textContent = 'Close';
  initTiles();
  layoutAll();
  syncTileColors();
}

// --- Input ---
function playSwap(from: number, to: number, solved: boolean): void {
  moveCount++;
  moveCountEl.textContent = String(moveCount).padStart(2, '0');

  swapInProgress = true;
  tiles[from].setSelected(false);
  tiles[to].setSelected(false);

  const gen  = gameGen;
  let done   = 0;
  beginAnim();
  const finish = () => {
    done++;
    if (done === 2) {
      endAnim();
      if (gameGen !== gen) return;
      const tmp    = tiles[from];
      tiles[from]  = tiles[to];
      tiles[to]    = tmp;
      tiles[from].gridPos = from;
      tiles[to].gridPos   = to;

      syncTileColors();
      swapInProgress = false;
      if (solved) triggerWin();
    }
  };

  tiles[from].animateTo(to, app.ticker, finish);
  tiles[to].animateTo(from, app.ticker, finish);
}

function onTileClick(gridPos: number): void {
  if (!game) return;
  if (swapInProgress) return;
  if (gridPos < 0 || gridPos >= game.total) return;
  const result = game.select(gridPos);

  if (result.action === 'select' || result.action === 'deselect') {
    syncTileColors();
  } else if (result.action === 'swap') {
    playSwap(result.from, result.to, result.solved);
  }
}

// --- Drag and drop ---
// A press only becomes a drag once it has moved far enough; anything shorter
// falls through to the tap-tap path, so either way of playing still works.
const DRAG_SLOP = 6;

interface Drag {
  tile: Tile;
  pointerId: number;
  startX: number;
  startY: number;
  moved: boolean;         // past the slop, so this is a drag not a tap
  target: number | null;  // grid position it would land on
}
let drag: Drag | null = null;

/** Grid position under a point in grid space, or null if that's not a tile. */
function tileAt(x: number, y: number): number | null {
  if (!game || tileSize === 0) return null;
  const step = tileSize + GAP;
  const col  = Math.floor(x / step);
  const row  = Math.floor(y / step);
  if (col < 0 || col >= game.gridW || row < 0 || row >= game.gridH) return null;
  const at = cellIndex.get(row * game.gridW + col);
  return at === undefined ? null : at;
}

function setDropTarget(next: number | null): void {
  if (!drag || drag.target === next) return;
  if (drag.target !== null) tiles[drag.target].setDropTarget(false);
  if (next !== null) tiles[next].setDropTarget(true);
  drag.target = next;
}

function endDrag(): void {
  if (drag?.target !== null && drag?.target !== undefined) {
    tiles[drag.target].setDropTarget(false);
  }
  if (drag?.moved) endAnim();
  drag = null;
}

function onTilePointerDown(tile: Tile, e: FederatedPointerEvent): void {
  if (!game || swapInProgress || drag) return;
  if (game.isPinned(tile.gridPos)) return;
  drag = {
    tile,
    pointerId: e.pointerId,
    startX: e.global.x,
    startY: e.global.y,
    moved: false,
    target: null,
  };
}

function onPointerMove(e: FederatedPointerEvent): void {
  if (!drag || e.pointerId !== drag.pointerId || !game) return;

  if (!drag.moved) {
    if (Math.hypot(e.global.x - drag.startX, e.global.y - drag.startY) < DRAG_SLOP) return;
    drag.moved = true;
    beginAnim();
    // Picking a tile up supersedes anything the taps had selected.
    game.clearSelection();
    syncTileColors();
    drag.tile.bringToFront();
  }

  const p = gridContainer.toLocal(e.global);
  drag.tile.dragTo(p.x, p.y);

  const over = tileAt(p.x, p.y);
  const droppable = over !== null && over !== drag.tile.gridPos && !game.isPinned(over);
  setDropTarget(droppable ? over : null);
}

function onPointerUp(e: FederatedPointerEvent): void {
  if (!drag || e.pointerId !== drag.pointerId) return;
  const { tile, moved, target } = drag;
  endDrag();

  if (!moved) {
    onTileClick(tile.gridPos);
    return;
  }

  const result = target === null ? null : game!.swap(tile.gridPos, target);
  if (result && result.action === 'swap') {
    playSwap(result.from, result.to, result.solved);
  } else {
    // Dropped on nothing — put it back where it came from.
    swapInProgress = true;
    beginAnim();
    tile.animateTo(tile.gridPos, app.ticker, () => { swapInProgress = false; endAnim(); });
  }
}

app.stage.eventMode = 'static';
app.stage.hitArea   = app.screen;
app.stage.on('pointermove',      onPointerMove);
app.stage.on('pointerup',        onPointerUp);
app.stage.on('pointerupoutside', onPointerUp);

// --- Background rainbow gradient: pulse on a click that doesn't hit a tile ---
const bgGradient = document.getElementById('bg-gradient')!;
app.stage.on('pointerdown', (e: FederatedPointerEvent) => {
  if (e.target !== app.stage) return;
  bgGradient.classList.remove('pulse');
  void bgGradient.offsetWidth;
  bgGradient.classList.add('pulse');
});

// --- Win ---
function triggerWin(): void {
  const { gridW, activeCells, total } = game!;
  beginAnim();
  let pulsesLeft = total;
  for (let i = 0; i < total; i++) {
    const flatPos = activeCells[i];
    const col   = flatPos % gridW;
    const row   = Math.floor(flatPos / gridW);
    const delay = (row + col) * 150;
    void tiles[i].pulse(app.ticker, delay).then(() => {
      if (--pulsesLeft === 0) endAnim();
    });
  }
  // Banked before the celebration runs, so a player who leaves mid-pulse still
  // gets the credit.
  if (appMode === 'prog' && level) recordSolve(loadProgress(), level.n, moveCount);

  const gen   = gameGen;
  const moves = moveCount;
  setTimeout(() => {
    if (gameGen !== gen) return; // left the board behind while it was pulsing
    document.getElementById('win-moves-count')!.textContent =
      String(moves).padStart(2, '0');
    winOverlay.classList.add('visible');
  }, 3000);
}

// --- Screens ---
const levelSelect = initLevelSelect(startLevel);
const winLevelEl  = document.getElementById('win-level')!;
const hudLevelEl  = document.getElementById('hud-level')!;
const restartBtn  = document.getElementById('restart-btn')!;

function setScreen(screen: Screen): void {
  document.body.dataset.screen = screen;
  // No point burning frames on a board nobody can see.
  if (screen === 'game') app.ticker.start();
  else                   app.ticker.stop();
}

function showMenu(): void {
  setScreen('menu');
}

function showLevels(): void {
  setScreen('levels');
  levelSelect.refresh();
}

function startFreePlay(): void {
  appMode = 'free';
  level   = null;
  // Playing a level overwrites mode/size/difficulty, so free play takes its
  // own back off the settings controls, which are the record of what it was
  // last set to. (Those live further down the file but are always initialised
  // by the time a button can be clicked.)
  gameMode  = modeToggle.dataset.mode as BoardMode;
  sizeIndex = Number(sizeSlider.value);
  diffIndex = Number(diffSlider.value);
  document.body.dataset.mode = 'free';
  restartBtn.textContent = 'Play Again';
  setScreen('game');
  newGame();
}

function startLevel(next: Level): void {
  appMode   = 'prog';
  level     = next;
  gameMode  = next.mode;
  sizeIndex = next.sizeIndex;
  diffIndex = next.diffIndex;
  document.body.dataset.mode = 'prog';
  hudLevelEl.textContent = `Level ${next.n}`;
  winLevelEl.textContent = `Level ${next.n}`;
  restartBtn.textContent = 'Levels';
  setScreen('game');
  newGame();
}

document.getElementById('btn-free')!.addEventListener('click', startFreePlay);
document.getElementById('btn-prog')!.addEventListener('click', showLevels);
document.getElementById('levels-back')!.addEventListener('click', showMenu);
document.getElementById('hud-back')!.addEventListener('click', () => {
  winOverlay.classList.remove('visible', 'peeking');
  if (appMode === 'prog') showLevels();
  else                    showMenu();
});

// --- Settings panel toggle ---
const settingsBtn   = document.getElementById('settings-btn')!;
const settingsPanel = document.getElementById('settings-panel')!;
settingsBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  const open = settingsPanel.classList.toggle('open');
  settingsBtn.setAttribute('aria-expanded', String(open));
  settingsBtn.classList.toggle('panel-open', open);
});
document.addEventListener('click', (e) => {
  if (settingsPanel.classList.contains('open') &&
      !settingsPanel.contains(e.target as Node) &&
      e.target !== settingsBtn) {
    settingsPanel.classList.remove('open');
    settingsBtn.setAttribute('aria-expanded', 'false');
    settingsBtn.classList.remove('panel-open');
  }
});

// --- Spin the restart/settings icons counter-clockwise on click ---
function spinIcon(el: HTMLElement): void {
  el.classList.remove('spin-ccw');
  // Force a reflow so the animation restarts if it's still mid-spin.
  void el.offsetWidth;
  el.classList.add('spin-ccw');
}
const restartIcon  = document.getElementById('restart-icon')!;
const settingsIcon = document.getElementById('settings-icon')!;
document.getElementById('hud-restart')!.addEventListener('click', () => spinIcon(restartIcon));
settingsBtn.addEventListener('click', () => spinIcon(settingsIcon));

// --- Mode toggle (Grid / Cross) ---
const modeToggle = document.getElementById('mode-toggle')!;
(document.querySelectorAll('.mode-toggle-opt') as NodeListOf<HTMLButtonElement>).forEach(btn => {
  btn.addEventListener('click', () => {
    const next = btn.dataset.mode as BoardMode;
    if (next === gameMode) return;
    gameMode = next;
    modeToggle.dataset.mode = gameMode;
    (document.querySelectorAll('.mode-toggle-opt') as NodeListOf<HTMLButtonElement>).forEach(b =>
      b.classList.toggle('active', b.dataset.mode === gameMode)
    );
    newGame();
  });
});

// --- Size / difficulty sliders ---
function setSliderFill(el: HTMLInputElement): void {
  const min = Number(el.min), max = Number(el.max), val = Number(el.value);
  el.style.setProperty('--fill', `${((val - min) / (max - min)) * 100}%`);
}

const sizeSlider = document.getElementById('size-slider') as HTMLInputElement;
setSliderFill(sizeSlider);
sizeSlider.addEventListener('input', () => {
  setSliderFill(sizeSlider);
  const i = Number(sizeSlider.value);
  if (i === sizeIndex) return;
  sizeIndex = i;
  newGame();
});

const diffSlider = document.getElementById('diff-slider') as HTMLInputElement;
setSliderFill(diffSlider);
diffSlider.addEventListener('input', () => {
  setSliderFill(diffSlider);
  const i = Number(diffSlider.value);
  if (i === diffIndex) return;
  diffIndex = i;
  newGame();
});

// --- Restart buttons ---
restartBtn.addEventListener('click', () => {
  winOverlay.classList.remove('visible', 'peeking');
  if (appMode === 'prog') showLevels();
  else                    newGame();
});
document.getElementById('hud-restart')!.addEventListener('click', newGame);

// --- Peek at the finished board ---
winHideBtn.addEventListener('click', () => {
  const peeking = winOverlay.classList.toggle('peeking');
  winHideBtn.textContent = peeking ? 'Show' : 'Close';
});

// --- Hint ---
const eyeWrap = document.querySelector('.eye-wrap')!;
document.getElementById('hud-hint')!.addEventListener('click', () => {
  eyeWrap.classList.remove('blink-strong');
  void (eyeWrap as HTMLElement).offsetWidth;
  eyeWrap.classList.add('blink-strong');
});
document.getElementById('hud-hint')!.addEventListener('click', () => {
  if (!game) return;
  if (swapInProgress) return;

  const result = game.hint();
  if (!result) return;

  moveCount++;
  moveCountEl.textContent = String(moveCount).padStart(2, '0');

  swapInProgress = true;
  syncTileColors();

  const { from, to, solved } = result;
  const gen  = gameGen;
  let done   = 0;
  beginAnim();
  const finish = () => {
    done++;
    if (done === 2) {
      endAnim();
      if (gameGen !== gen) return;
      const tmp    = tiles[from];
      tiles[from]  = tiles[to];
      tiles[to]    = tmp;
      tiles[from].gridPos = from;
      tiles[to].gridPos   = to;

      syncTileColors();
      swapInProgress = false;
      if (solved) triggerWin();
    }
  };

  tiles[from].animateTo(to, app.ticker, finish);
  tiles[to].animateTo(from, app.ticker, finish);
});

// --- Resize ---
// Pixi's own resize is queued on a requestAnimationFrame, and rAF doesn't run
// while the page is hidden — so by the time a `resize` fires, app.screen can be
// a frame, or a whole app switch, out of date. Switching away from the browser
// on a phone reports a viewport of almost nothing on the way out, and if the
// board is laid out against that, nothing arrives afterwards to put it right:
// it just stays tiny, on a page that can't be pinch-zoomed. So the renderer is
// brought up to date first, sizes that can't be real are ignored, and every
// event that means "the viewport may have changed" gets a fresh layout.
function relayout(): void {
  app.resize();
  app.stage.hitArea = app.screen;
  layoutAll();
}

window.addEventListener('resize', relayout);
window.addEventListener('orientationchange', relayout);
window.addEventListener('pageshow', relayout);
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) relayout();
});
window.visualViewport?.addEventListener('resize', relayout);

// --- Dev hook ---
// Solving a board by hand every time you want to see what happens afterwards
// gets old fast. Dev builds only.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__chroma = {
    get game() { return game; },
    get level() { return level; },
    winNow() {
      if (!game || swapInProgress) return;
      for (let i = 0; i < game.total; i++) game.tileAt[i] = i;
      syncTileColors();
      triggerWin();
    },
  };
}

// --- Boot ---
showMenu();

})();
